import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Volume2, VolumeX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as signalR from '@microsoft/signalr';
import { useAppAuth } from '../hooks/useAppAuth';
import { notificationsApi, createSignalRConnection } from '../services/api';
import type { NotificationDto } from '../types/modules';
import { NotificationEventType } from '../types/modules';

// ── Notification event metadata ───────────────────────────────────────────────

const EVENT_ICON: Partial<Record<NotificationEventType, string>> = {
  [NotificationEventType.JobAlertMatch]:       '🔔',
  [NotificationEventType.Outbid]:              '⚡',
  [NotificationEventType.JobApproved]:         '✅',
  [NotificationEventType.QuoteReceived]:       '💬',
  [NotificationEventType.QuoteAccepted]:       '🤝',
  [NotificationEventType.ContractGenerated]:   '📄',
  [NotificationEventType.PaymentReleased]:     '💰',
  [NotificationEventType.ReferralEarning]:     '🎁',
  [NotificationEventType.DocumentExpiry30]:    '📅',
  [NotificationEventType.DocumentExpiry15]:    '📅',
  [NotificationEventType.DocumentExpiry7]:     '⚠️',
  [NotificationEventType.DocumentExpiry1]:     '🚨',
  [NotificationEventType.InsuranceExpiry30]:   '📅',
  [NotificationEventType.InsuranceExpiry15]:   '📅',
  [NotificationEventType.InsuranceExpiry7]:    '⚠️',
  [NotificationEventType.InsuranceExpiry1]:    '🚨',
  [NotificationEventType.VerificationComplete]: '🛡️',
  [NotificationEventType.DisputeOpened]:       '🔴',
  [NotificationEventType.DisputeResolved]:     '✅',
  [NotificationEventType.DriverLoggedIn]:      '🚗',
};

const EVENT_LABEL: Partial<Record<NotificationEventType, string>> = {
  [NotificationEventType.JobAlertMatch]:       'New job match',
  [NotificationEventType.Outbid]:              'Outbid',
  [NotificationEventType.JobApproved]:         'Job approved',
  [NotificationEventType.QuoteReceived]:       'New quote',
  [NotificationEventType.QuoteAccepted]:       'Quote accepted',
  [NotificationEventType.ContractGenerated]:   'Contract ready',
  [NotificationEventType.PaymentReleased]:     'Payment released',
  [NotificationEventType.ReferralEarning]:     'Referral income',
  [NotificationEventType.DocumentExpiry30]:    'Document expiry (30d)',
  [NotificationEventType.DocumentExpiry15]:    'Document expiry (15d)',
  [NotificationEventType.DocumentExpiry7]:     'Document expiry (7d)',
  [NotificationEventType.DocumentExpiry1]:     'Document expires tomorrow',
  [NotificationEventType.InsuranceExpiry30]:   'Insurance expiry (30d)',
  [NotificationEventType.InsuranceExpiry15]:   'Insurance expiry (15d)',
  [NotificationEventType.InsuranceExpiry7]:    'Insurance expiry (7d)',
  [NotificationEventType.InsuranceExpiry1]:    'Insurance expires tomorrow',
  [NotificationEventType.VerificationComplete]: 'Verification approved',
  [NotificationEventType.DisputeOpened]:       'Dispute raised',
  [NotificationEventType.DisputeResolved]:     'Dispute resolved',
  [NotificationEventType.DriverLoggedIn]:      'Driver logged in',
};

// ── Job-view suppression ─────────────────────────────────────────────────────
// When the carrier opens a job detail page, that job's ID is added here.
// Voice notifications for already-viewed jobs are then silenced.
const _viewedJobIds = new Set<string>();
export function markJobViewed(jobId: string) { _viewedJobIds.add(jobId); }

// SignalR events that should refresh the notification count
const NOTIFICATION_EVENTS = [
  'JobAlertMatch', 'Outbid', 'JobApproved', 'QuoteReceived', 'QuoteAccepted',
  'ContractGenerated', 'PaymentReleased', 'ReferralEarning',
  'DocumentExpiryWarning', 'VerificationComplete', 'InsuranceExpiry',
  'DisputeOpened', 'DisputeResolved', 'DriverLoggedIn',
];

// ── Component ─────────────────────────────────────────────────────────────────

export function NotificationBell() {
  const { account, getToken, isAuthenticated } = useAppAuth();
  const [open, setOpen]   = useState(false);
  const ref               = useRef<HTMLDivElement>(null);
  const navigate          = useNavigate();
  const qc                = useQueryClient();

  // ── Voice notifications — localStorage flag, default ON ──────────────
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem('td-voice-notif') !== '0');
  const voiceOnRef = useRef(voiceOn);
  voiceOnRef.current = voiceOn;

  // Holds a message that arrived while the tab was hidden; spoken on visibility restore
  const pendingVoiceRef = useRef<string | null>(null);

  const speakText = useCallback((text: string) => {
    setTimeout(() => {
      const utt = new SpeechSynthesisUtterance(text);
      utt.rate = 0.95;
      const voices = window.speechSynthesis.getVoices();
      const voice = voices.find(v => v.lang.startsWith('en-GB'))
        ?? voices.find(v => v.lang.startsWith('en'))
        ?? voices[0]
        ?? null;
      if (voice) utt.voice = voice;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utt);
    }, 0);
  }, []);

  // iOS Safari requires speechSynthesis to be called within a user gesture before
  // async calls (SignalR callbacks) are allowed to use it.
  const iosSpeechUnlockRef = useRef(false);
  const unlockSpeech = useCallback(() => {
    if (iosSpeechUnlockRef.current || !window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance('');
    window.speechSynthesis.speak(u);
    setTimeout(() => window.speechSynthesis.cancel(), 50);
    iosSpeechUnlockRef.current = true;
  }, []);

  const toggleVoice = useCallback(() => {
    setVoiceOn(v => {
      const next = !v;
      localStorage.setItem('td-voice-notif', next ? '1' : '0');
      voiceOnRef.current = next;
      if (next) unlockSpeech();
      else window.speechSynthesis?.cancel();
      return next;
    });
  }, [unlockSpeech]);

  // Pre-warm speech synthesis voices — Chrome loads them asynchronously;
  // calling speak() before they load selects no voice and silently fails.
  useEffect(() => {
    if (!window.speechSynthesis) return;
    const load = () => window.speechSynthesis.getVoices();
    load();
    window.speechSynthesis.addEventListener('voiceschanged', load);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', load);
  }, []);

  // iOS Safari blocks speechSynthesis in async callbacks (SignalR) unless it was
  // first called from a synchronous user gesture. Unlock on the first tap/click so
  // that job notifications arriving via SignalR can speak immediately.
  useEffect(() => {
    if (!voiceOn) return;
    const handler = () => unlockSpeech();
    document.addEventListener('touchstart', handler, { once: true });
    document.addEventListener('click',      handler, { once: true });
    return () => {
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('click',      handler);
    };
  }, [voiceOn, unlockSpeech]);

  // ── Unread count (polls every 30s) ────────────────────────────────────
  const { data: unreadCount = 0 } = useQuery<number>({
    queryKey: ['notifications', 'unread'],
    queryFn:  async () => {
      const token = await getToken();
      const res   = await notificationsApi.getUnreadCount(token);
      return res.count;
    },
    refetchInterval: 30_000,
    enabled: !!account,
  });

  // ── Recent notifications (fetched when dropdown opens) ────────────────
  const { data: recent = [] } = useQuery<NotificationDto[]>({
    queryKey: ['notifications', 'recent'],
    queryFn:  async () => {
      const token = await getToken();
      return notificationsApi.getMine(token);
    },
    select: items => items.slice(0, 6),
    enabled: open && !!account,
    staleTime: 10_000,
  });

  // ── SignalR — subscribe to JobHub for all user-targeted notification events
  // Gate on account.userId (not isAuthenticated) so we only start after
  // MSAL has fully initialised — isAuthenticated can be true from cached state
  // before acquireTokenSilent is ready, causing uninitialized_public_client errors.
  const accountId = account?.userId;
  useEffect(() => {
    if (!accountId) return;

    const conn = createSignalRConnection('/hubs/jobs', getToken);

    const handle = (data: unknown) => {
      qc.invalidateQueries({ queryKey: ['notifications', 'unread'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'recent'] });
      if (voiceOnRef.current && window.speechSynthesis) {
        const notifJobId = typeof (data as any)?.jobId === 'string' ? (data as any).jobId as string : null;
        const text = typeof (data as any)?.message === 'string' ? (data as any).message as string : null;
        if (text && !(notifJobId && _viewedJobIds.has(notifJobId))) {
          if (document.hidden) {
            // Tab is in background — speechSynthesis.speak() is silently blocked by browsers;
            // queue it and speak when the carrier switches back to this tab.
            pendingVoiceRef.current = text;
          } else {
            speakText(text);
          }
        }
      }
    };

    NOTIFICATION_EVENTS.forEach(evt => conn.on(evt, handle));
    conn.start().catch(console.error);

    return () => {
      NOTIFICATION_EVENTS.forEach(evt => conn.off(evt));
      conn.stop().catch(console.error);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // ── Speak queued voice notification when tab regains focus ───────────
  useEffect(() => {
    const onVisible = () => {
      if (document.hidden || !pendingVoiceRef.current || !voiceOnRef.current || !window.speechSynthesis) return;
      const text = pendingVoiceRef.current;
      pendingVoiceRef.current = null;
      speakText(text);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [speakText]);

  // ── Close on outside click ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = useCallback(async (n: NotificationDto) => {
    if (!n.isRead) {
      const token = await getToken();
      await notificationsApi.markRead(n.id, token).catch(() => {});
      qc.invalidateQueries({ queryKey: ['notifications', 'unread'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'recent'] });
    }
    if (n.eventType === NotificationEventType.DriverLoggedIn) navigate('/fleet-map');
    else if (n.jobId) navigate(`/jobs/${n.jobId}`);
    setOpen(false);
  }, [getToken, navigate, qc]);

  const markAllRead = useCallback(async () => {
    const token = await getToken();
    await notificationsApi.markAllRead(token).catch(() => {});
    qc.invalidateQueries({ queryKey: ['notifications'] });
    setOpen(false);
  }, [getToken, qc]);

  if (!account) return null;

  return (
    <div ref={ref} className="relative">
      {/* Bell button */}
      <button
        onClick={() => {
          setOpen(o => !o);
          // iOS Safari: unlock speech synthesis on first bell tap if voice is already on
          if (voiceOnRef.current) unlockSpeech();
        }}
        aria-label="Notifications"
        className="relative p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
      >
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-blue-600' : ''}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">
              Notifications {unreadCount > 0 && (
                <span className="ml-1.5 text-xs bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleVoice}
                title={voiceOn ? 'Mute voice alerts' : 'Unmute voice alerts'}
                className={`p-1 rounded transition-colors ${voiceOn ? 'text-indigo-600 hover:bg-indigo-50' : 'text-gray-400 hover:bg-gray-100'}`}
              >
                {voiceOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Notification list */}
          <div className="divide-y divide-gray-50 max-h-[360px] overflow-y-auto">
            {recent.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-400">
                <Bell className="w-7 h-7 mx-auto mb-2 opacity-30" />
                You're all caught up!
              </div>
            ) : recent.map(n => (
              <button
                key={n.id}
                onClick={() => markRead(n)}
                className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${!n.isRead ? 'bg-blue-50/60' : ''}`}
              >
                <span className="text-lg mt-0.5 shrink-0">
                  {EVENT_ICON[n.eventType] ?? '🔔'}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-snug ${!n.isRead ? 'font-semibold text-gray-900' : 'font-normal text-gray-700'}`}>
                    {n.subject || EVENT_LABEL[n.eventType] || 'Notification'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{n.body}</p>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {new Date(n.createdAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                  </p>
                </div>
                {!n.isRead && (
                  <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-1.5" />
                )}
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100">
            <button
              onClick={() => { setOpen(false); navigate('/notifications'); }}
              className="w-full py-3 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 font-medium transition-colors"
            >
              View all notifications →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
